import { useCallback, useEffect, useRef, useState } from 'react'
import { resolveDragAxis } from './dragGesture'
import {
  PULL_TRIGGER_DISTANCE,
  canArmPull,
  resolvePullArmed,
  resolvePullCommit,
  resolvePullDistance,
} from './pullGesture'
import { pullAngle, releaseOmega } from './pullMotion'
import { fireLightHaptic } from './sharedElementTransition'
import { prefersReducedMotion } from './scoreCountUp'

export interface PullRenderState {
  phase: 'idle' | 'pulling' | 'refreshing'
  /** How far the content is pulled down, in px. */
  distance: number
  /** Past the trigger point — releasing now refreshes. */
  armed: boolean
  /** Angular velocity (deg/ms) the finger left the ring turning at, handed over at release so the
   *  spin continues from it instead of restarting. Zero at every other moment. */
  releaseOmega: number
  /** False under prefers-reduced-motion: the caller shows the indicator without the elastic
   *  content travel. */
  elastic: boolean
}

const IDLE_STATE: PullRenderState = {
  phase: 'idle',
  distance: 0,
  armed: false,
  releaseOmega: 0,
  elastic: true,
}

/** The arm tick and the release confirmation, in ms. Deliberately different weights: a quick
 *  pull-and-let-go fires both inside ~100ms. */
export const PULL_ARM_HAPTIC_MS = 8
export const PULL_RELEASE_HAPTIC_MS = 18

function now(): number {
  const perf = (globalThis as { performance?: { now?: () => number } }).performance
  return typeof perf?.now === 'function' ? perf.now() : Date.now()
}

export interface UsePullToRefreshOptions {
  /** Runs on a committed pull. The indicator stays up until the returned promise settles. */
  onRefresh: () => Promise<void> | void
  /** Off while another gesture owns the screen (an open modal, a running transition). */
  enabled?: boolean
}

/**
 * Pull-to-refresh for the league screens.
 *
 * Two things make this more than a `touchmove` listener. It shares the screen with two horizontal
 * gestures — the tab drag-nav and swipe-to-go-back — so it resolves the axis with the same
 * `resolveDragAxis` they do and stands down the moment a gesture reads as horizontal. And the
 * *document* scrolls on these routes (there is no inner scroller), so it arms off `window.scrollY`
 * and has to `preventDefault` a non-passive `touchmove`, or iOS rubber-bands the whole page behind
 * the indicator.
 */
export function usePullToRefresh({ onRefresh, enabled = true }: UsePullToRefreshOptions): {
  containerRef: (el: HTMLDivElement | null) => void
  pull: PullRenderState
} {
  // Same node-in-state pattern as `useDragNav`: React 18 StrictMode replays ref callbacks
  // independently of effect timing, and a plain ref can be null when the effect runs.
  const [node, setNode] = useState<HTMLDivElement | null>(null)
  const containerRef = useCallback((el: HTMLDivElement | null) => setNode(el), [])
  const [state, setState] = useState<PullRenderState>(IDLE_STATE)

  const onRefreshRef = useRef(onRefresh)
  const enabledRef = useRef(enabled)
  useEffect(() => {
    onRefreshRef.current = onRefresh
    enabledRef.current = enabled
  })

  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const rafRef = useRef<number | null>(null)
  const refreshingRef = useRef(false)
  const gestureRef = useRef<{
    active: boolean
    startX: number
    startY: number
    axis: 'horizontal' | 'vertical' | null
    distance: number
    armed: boolean
    elastic: boolean
    /** The two most recent finger-driven ring angles, for the handover at release. */
    samples: Array<{ angle: number; time: number }>
  } | null>(null)

  useEffect(() => {
    const el = node
    if (!el) return

    const cancelFrame = () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }

    const onTouchStart = (e: TouchEvent) => {
      gestureRef.current = null
      if (!enabledRef.current || refreshingRef.current) return
      if (e.touches.length !== 1) return
      // Armed off the position at touch-down, like every native implementation: flinging up to
      // the top and continuing into a pull in one gesture is a scroll, not a refresh.
      if (!canArmPull(window.scrollY)) return
      const touch = e.touches[0]
      gestureRef.current = {
        active: true,
        startX: touch.clientX,
        startY: touch.clientY,
        axis: null,
        distance: 0,
        armed: false,
        elastic: !prefersReducedMotion(),
        samples: [{ angle: 0, time: now() }],
      }
    }

    const onTouchMove = (e: TouchEvent) => {
      const gesture = gestureRef.current
      if (!gesture || !gesture.active) return
      const touch = e.touches[0]
      if (!touch) return
      const dx = touch.clientX - gesture.startX
      const dy = touch.clientY - gesture.startY

      if (gesture.axis === null) {
        const axis = resolveDragAxis(dx, dy)
        if (axis === null) return
        // A tab swipe or an edge-back drag. Stand down entirely rather than run alongside it.
        if (axis === 'horizontal' || dy <= 0) {
          gesture.active = false
          return
        }
        gesture.axis = 'vertical'
      }

      // Ours for the rest of the gesture — hold off the document's own overscroll.
      e.preventDefault()
      gesture.distance = resolvePullDistance(dy)
      const armed = resolvePullArmed(gesture.distance, gesture.armed)
      if (armed && !gesture.armed) {
        // The pull has reached the point where letting go does something.
        fireLightHaptic(PULL_ARM_HAPTIC_MS)
      }
      gesture.armed = armed
      gesture.samples = [
        gesture.samples[gesture.samples.length - 1],
        { angle: pullAngle(gesture.distance), time: now() },
      ]

      if (rafRef.current !== null) return
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null
        setState({
          phase: 'pulling',
          distance: gesture.distance,
          armed: gesture.armed,
          releaseOmega: 0,
          elastic: gesture.elastic,
        })
      })
    }

    const onTouchEnd = () => {
      const gesture = gestureRef.current
      gestureRef.current = null
      cancelFrame()
      if (!gesture || !gesture.active || gesture.axis !== 'vertical') return

      if (!resolvePullCommit(gesture.distance)) {
        // Released short: retract, fire nothing.
        setState({ ...IDLE_STATE, elastic: gesture.elastic })
        return
      }

      // Committing to a round trip is the moment worth confirming, and it is heavier than the arm
      // tick so the two never read as one stutter.
      fireLightHaptic(PULL_RELEASE_HAPTIC_MS)
      refreshingRef.current = true
      setState({
        phase: 'refreshing',
        distance: PULL_TRIGGER_DISTANCE,
        armed: true,
        releaseOmega: releaseOmega(gesture.samples[0], gesture.samples[1] ?? gesture.samples[0]),
        elastic: gesture.elastic,
      })
      // Nothing fires when the data lands: a third buzz on a response the user didn't ask to feel
      // is noise, and the failure toast stays silent too.
      const settle = () => {
        refreshingRef.current = false
        if (mountedRef.current) {
          setState({ ...IDLE_STATE, elastic: gesture.elastic })
        }
      }
      Promise.resolve(onRefreshRef.current()).then(settle, settle)
    }

    const onTouchCancel = () => {
      const gesture = gestureRef.current
      gestureRef.current = null
      cancelFrame()
      if (!refreshingRef.current) {
        setState({ ...IDLE_STATE, elastic: gesture?.elastic ?? true })
      }
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd, { passive: true })
    el.addEventListener('touchcancel', onTouchCancel, { passive: true })

    return () => {
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
      el.removeEventListener('touchcancel', onTouchCancel)
      cancelFrame()
    }
  }, [node])

  return { containerRef, pull: state }
}
